import { useState, useEffect, useCallback } from 'react'
import { cartApi, Cart } from '../services/api'

interface UseCartReturn {
  cart: Cart | null
  loading: boolean
  error: string | null
  addToCart: (productId: string, quantity?: number) => Promise<void>
  updateQuantity: (itemId: string, quantity: number) => Promise<void>
  removeItem: (itemId: string) => Promise<void>
  clearCart: () => Promise<void>
  refreshCart: () => Promise<void>
  getItemQuantity: (productId: string) => number
  getTotalItems: () => number
  isItemInCart: (productId: string) => boolean
}

export function useCart(): UseCartReturn {
  const [cart, setCart] = useState<Cart | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load cart data
  const refreshCart = useCallback(async () => {
    try {
      console.log('useCart: Starting to load cart...')
      setLoading(true)
      setError(null)
      const cartData = await cartApi.getCart()
      console.log('useCart: Cart data loaded successfully:', cartData)
      setCart(cartData)
    } catch (err) {
      // Keep the technical detail in the console, but never surface raw backend
      // strings (e.g. "Authorization header is required") to the user.
      console.error('useCart: Error loading cart:', err)
      setError("We couldn't load your cart. Please try again.")
    } finally {
      setLoading(false)
      console.log('useCart: Loading finished')
    }
  }, [])

  // Add item to cart
  const addToCart = useCallback(async (productId: string, quantity: number = 1) => {
    try {
      setLoading(true)
      setError(null)

      const result = await cartApi.addToCart({
        product_id: productId,
        quantity,
      })

      // Show success feedback
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success')
      }

      // Refresh cart to get updated data
      await refreshCart()

      // Show visual feedback (you can customize this)
      console.log('Added to cart:', result.message)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add item to cart'
      setError(errorMessage)
      console.error('Failed to add to cart:', err)

      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error')
      }

      throw err
    } finally {
      setLoading(false)
    }
  }, [refreshCart])

  // Update item quantity
  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    try {
      setLoading(true)
      setError(null)

      if (quantity <= 0) {
        await cartApi.removeCartItem(itemId)
      } else {
        await cartApi.updateCartItem(itemId, { quantity })
      }

      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
      }

      await refreshCart()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update quantity'
      setError(errorMessage)
      console.error('Failed to update quantity:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [refreshCart])

  // Remove item from cart
  const removeItem = useCallback(async (itemId: string) => {
    try {
      setLoading(true)
      setError(null)

      await cartApi.removeCartItem(itemId)

      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('medium')
      }

      await refreshCart()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove item'
      setError(errorMessage)
      console.error('Failed to remove item:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [refreshCart])

  // Clear entire cart
  const clearCart = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      await cartApi.clearCart()

      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('heavy')
      }

      await refreshCart()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear cart'
      setError(errorMessage)
      console.error('Failed to clear cart:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [refreshCart])

  // Get quantity of specific product in cart
  const getItemQuantity = useCallback((productId: string): number => {
    if (!cart?.items) return 0
    const item = cart.items.find(item => item.product_id === productId)
    return item?.quantity || 0
  }, [cart])

  // Get total number of items in cart
  const getTotalItems = useCallback((): number => {
    if (!cart?.items) return 0
    return cart.items.reduce((total, item) => total + item.quantity, 0)
  }, [cart])

  // Check if product is in cart
  const isItemInCart = useCallback((productId: string): boolean => {
    return getItemQuantity(productId) > 0
  }, [getItemQuantity])

  // Load cart on mount
  useEffect(() => {
    refreshCart()
  }, [refreshCart])

  return {
    cart,
    loading,
    error,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    refreshCart,
    getItemQuantity,
    getTotalItems,
    isItemInCart,
  }
}